import * as React from 'react';

import PropTypes from 'prop-types';
import { compose, mapProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';
import connectToStores from 'js/lib/connectToStores';
import pendo, { PARTNER_API_KEY, getPartnerAccountData } from 'js/lib/pendo';
import user, { isAuthenticatedUser, isPoweruser } from 'js/lib/user';

import WithHeartbeat from 'bundles/admin-shared/analytics/WithHeartbeat';
import { ROUTE_ARGUMENTS } from 'bundles/admin-shared/analytics/constants';
import { A11yAnnouncerProvider } from 'bundles/admin-shared/components/A11yAnnouncer';
import { AdminV2PartnerProvider } from 'bundles/admin-shared/hooks/usePartner';
import NewPartnerBar from 'bundles/admin-v2/components/NewPartnerBar';
import { withPartnersByUser } from 'bundles/admin-v2/components/data/fetchPartners';
import { ACCEPT_INVITE } from 'bundles/admin-v2/constants/routes';
import type { Partner as AdminPartner } from 'bundles/admin-v2/constants/types';
import Partner from 'bundles/author-common/models/Partner';
import { NotFound } from 'bundles/common/components/withNotFound';
import AdminPageHeader from 'bundles/composable-page-header-admin/header-renderers/AdminPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import Naptime from 'bundles/naptimejs';
import UserPreferencesV1 from 'bundles/naptimejs/resources/userPreferences.v1';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import { isPendoEnabledForTeachAndAdmin } from 'bundles/teach-course/utils/analyticsUtils';
import AuthenticationOption from 'bundles/user-account/components/AuthenticationOption';
import UserPermissions from 'bundles/user-permissions/src/UserPermissions';
import UserPermissionsDataContext from 'bundles/user-permissions/src/UserPermissionsDataContext';

type PropsFromGraphQL = {
  partners: Array<AdminPartner> | undefined;
};

type Props = {
  requestCountryCode?: string | null;
  csrfToken?: string | null;
  children: JSX.Element;

  selectedPartner: AdminPartner | undefined;
  pathname: string | undefined;
} & Naptime.AddedProps &
  PropsFromGraphQL;

function isInstitutionUrl(pathname: string | undefined) {
  if (!pathname) {
    return false;
  }
  return pathname.includes('institution');
}

// map v2 partner model into the legacy author-common partner one due to pendo being unable to
// handle v2 model.
const partnersToAdminPartners = (adminPartners: Array<AdminPartner> = []): Array<Partner> => {
  return adminPartners
    ?.filter((partner) => partner.partnerId)
    .map(
      (partner) =>
        new Partner({
          id: partner.partnerId.toString(),
          name: partner.partnerName as string,
          shortName: partner.shortName as string,
          squareLogo: partner.squareLogo as string,
        })
    );
};

export class AdminV2App extends React.Component<Props, {}> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AdminV2App.contextTypes>;

  static childContextTypes = {
    csrfToken: PropTypes.string,
    requestCountryCode: PropTypes.string,
  };

  componentDidMount() {
    if (!isAuthenticatedUser()) {
      return;
    }
    if (this.props.partners) {
      this.initializePendo();
    }
    // [fe-tech-debt] Ideally we shouldn't need this as it may result in duplicate requests to userPreferences.v1
    // However this is a possibility that the selectedPartner props is already valid and no further props update would happen
    // thus, the logic inside componentDidUpdate() may not be triggered. We should find a way to chain the requests
    // of GetPartnersByUser and userPreferences.v1 properly
    const { naptime, selectedPartner } = this.props;
    const key = UserPreferencesV1.keys.ADMIN_SELECTED_PARTNER;

    if (selectedPartner?.partnerId) {
      naptime.executeMutation(UserPreferencesV1.updatePreference(key, { partnerId: selectedPartner.partnerId }));
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!isAuthenticatedUser()) {
      return;
    }
    const { naptime, selectedPartner } = this.props;
    const key = UserPreferencesV1.keys.ADMIN_SELECTED_PARTNER;
    if (!prevProps.selectedPartner && !!selectedPartner?.partnerId) {
      naptime.executeMutation(UserPreferencesV1.updatePreference(key, { partnerId: selectedPartner.partnerId }));
    }

    if (!prevProps.partners && this.props.partners) {
      this.initializePendo();
    }
  }

  initializePendo() {
    const { partners } = this.props;
    const adminPartners = partnersToAdminPartners(partners);

    const { external_id: externalId, is_superuser: isSuperuser, is_staff: isStaff } = user.get();
    const path = window.location.href;

    if (isPendoEnabledForTeachAndAdmin()) {
      pendo.init(
        {
          visitor: {
            id: externalId,
            isSuperuser,
            isStaff,
            path,
          },
          account: getPartnerAccountData({ partners: adminPartners, isSuperuser }),
        },
        PARTNER_API_KEY
      );
    }
  }

  render() {
    const { children, selectedPartner, partners, pathname } = this.props;

    if (pathname?.includes(ACCEPT_INVITE)) {
      return children;
    }
    if (!isAuthenticatedUser()) {
      return <AuthenticationOption onCloseRedirectURL="/" defaultMode="login" />;
    }
    if (!partners) {
      // preventing a flash of not found in CSR
      return null;
    }
    if (partners.length === 0 || !selectedPartner?.partnerId) {
      return <NotFound />;
    }

    return (
      <AdminV2PartnerProvider>
        <WithHeartbeat />
        <A11yAnnouncerProvider>
          <div className="rc-AdminApp-v2">
            <UserPermissions>
              <UserPermissionsDataContext>
                <UniversalPageFeatures />
                <AdminPageHeader />
                {isPoweruser() && isInstitutionUrl(pathname) && <NewPartnerBar />}
                {children &&
                  React.cloneElement(children, {
                    selectedPartnerId: selectedPartner.partnerId,
                    selectedPartner,
                    partners,
                  })}
              </UserPermissionsDataContext>
            </UserPermissions>
          </div>
        </A11yAnnouncerProvider>
      </AdminV2PartnerProvider>
    );
  }
}

export default compose<Props, {}>(
  withPartnersByUser,
  connectToStores<Props, Props>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  })),
  connectToRouter((router) => {
    return { shortName: router.params[ROUTE_ARGUMENTS.INSTITUTION_SLUG], pathname: router.location.pathname };
  }),
  mapProps(({ partnersByUser, shortName, ...rest }: { partnersByUser?: Array<Partner>; shortName: string }) => {
    return {
      selectedPartner: partnersByUser?.find((partner) => partner.shortName === shortName),
      partners: partnersByUser,
      ...rest,
    };
  }),
  Naptime.createContainer(() => ({}))
)(AdminV2App);
